import React, { useState } from 'react';
import { ChevronLeft, Zap, Check } from 'lucide-react';
import { useAuth } from '../../firebase/AuthContext';
import { useNavigate } from 'react-router-dom';

/**
 * Pricing Page Component
 * Displays pricing tiers and free trial banner
 */
const PricingPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      price: 20,
      period: 'month',
      description: 'Perfect for ongoing projects',
      features: [
        'Unlimited documents',
        'Case management',
        'PDF stamping',
        'Document collaboration',
        'Priority support',
      ],
      buttonText: 'Subscribe Monthly',
      color: 'app-info',
    },
    {
      id: 'annual',
      name: 'Annual',
      price: 200,
      period: 'year',
      description: 'Best value - Save 17%',
      features: [
        'Everything in Monthly',
        'Advanced analytics',
        'Custom templates',
        'Bulk operations',
        'Dedicated support',
      ],
      buttonText: 'Subscribe Annually',
      color: 'app-accent',
      popular: true,
    },
  ];

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    // TODO: Integrate with Stripe or payment provider
    console.log(`Selected plan: ${planId}`);
  };

  const handleStartTrial = () => {
    // TODO: Integrate with trial start logic
    console.log('Starting 14-day free trial');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="h-screen bg-app-white flex flex-col">
      {/* Header */}
      <div className="border-b border-app-gray-200 flex-shrink-0">
        <div className="max-w-6xl mx-auto px-app-2xl py-app-2xl">
          {/* Logo */}
          <div className="mb-app-2xl">
            <img
              src="/logo.svg"
              alt="Law Stamp Logo"
              className="h-14 w-auto"
            />
          </div>

          {/* Back Button and Heading */}
          <button
            onClick={handleBack}
            className="flex items-center gap-app-sm text-app-info hover:text-app-info hover:opacity-75 transition-opacity mb-app-2xl"
          >
            <ChevronLeft className="w-comp-lg h-comp-lg" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-app-gray-900 mb-app-md">
            Simple, Transparent Pricing
          </h1>
          <p className="text-app-lg text-app-gray-600">
            Choose the perfect plan for your legal document management needs
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-app-2xl py-app-4xl">
        
        {/* Free Trial Banner */}
        <div className="bg-gradient-to-r from-app-info to-blue-600 rounded-lg p-app-3xl text-app-white text-center">
          <h2 className="text-3xl font-bold mb-app-md">
            Try it!
          </h2>
          <p className="text-lg mb-app-2xl opacity-95">
            Try Stampstack free for 14 days. No credit card required, no strings attached.
          </p>
          <button
            onClick={handleStartTrial}
            className="bg-app-white text-app-info font-semibold py-app-md px-app-3xl rounded-app-sm hover:opacity-90 transition-opacity inline-flex items-center gap-app-sm"
          >
            <Zap className="w-comp-lg h-comp-lg" />
            Start 14-Day Free Trial
          </button>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-app-3xl mb-app-4xl">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`
                relative rounded-lg border-2 overflow-hidden transition-all duration-app-normal
                ${
                  plan.popular
                    ? `border-app-${plan.color} bg-app-${plan.color} bg-opacity-5 shadow-lg`
                    : `border-app-gray-200 bg-app-white hover:border-app-gray-300`
                }
              `}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className={`absolute top-0 right-0 bg-app-${plan.color} text-app-white px-app-lg py-app-sm text-app-xs font-semibold rounded-bl-lg`}>
                  MOST POPULAR
                </div>
              )}

              <div className={`p-app-3xl ${plan.popular ? `border-b-2 border-app-${plan.color} border-opacity-20` : ''}`}>
                {/* Plan Header */}
                <h3 className="text-2xl font-bold text-app-gray-900 mb-app-sm">
                  {plan.name}
                </h3>
                <p className="text-app-sm text-app-gray-600 mb-app-2xl">
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-app-2xl">
                  <span className={`text-5xl font-bold text-app-${plan.color}`}>
                    ${plan.price}
                  </span>
                  <span className="text-app-gray-600 ml-app-sm">
                    /{plan.period}
                  </span>
                  <p className="text-app-xs text-app-gray-500 mt-app-sm">
                    Billed {plan.period === 'year' ? 'annually' : 'monthly'}
                  </p>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-app-md px-app-lg rounded-app-sm font-semibold transition-all duration-app-normal flex items-center justify-center gap-app-sm ${
                    plan.popular
                      ? `bg-app-${plan.color} text-app-white hover:opacity-90 btn-primary`
                      : `bg-app-gray-100 text-app-gray-900 hover:bg-app-gray-200`
                  }`}
                >
                  <Zap className="w-comp-lg h-comp-lg" />
                  {plan.buttonText}
                </button>
              </div>

              {/* Features List */}
              <div className="p-app-3xl">
                <p className="text-app-xs font-semibold text-app-gray-500 uppercase tracking-wide mb-app-lg">
                  What's included
                </p>
                <ul className="space-y-app-md">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-app-md">
                      <Check className={`w-comp-lg h-comp-lg text-app-success flex-shrink-0 mt-0.5`} />
                      <span className="text-app-sm text-app-gray-700">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-app-4xl pt-app-3xl border-t border-app-gray-200">
          <h3 className="text-2xl font-bold text-app-gray-900 mb-app-2xl">
            Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-app-2xl">
            <div>
              <h4 className="font-semibold text-app-gray-900 mb-app-sm">
                Can I change my plan?
              </h4>
              <p className="text-app-sm text-app-gray-600">
                Yes, you can upgrade or downgrade your plan at any time. Changes take effect on your next billing cycle.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-app-gray-900 mb-app-sm">
                Is there a free trial?
              </h4>
              <p className="text-app-sm text-app-gray-600">
                Yes! Every new account gets a 14-day free trial with full access to all features.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-app-gray-900 mb-app-sm">
                What payment methods do you accept?
              </h4>
              <p className="text-app-sm text-app-gray-600">
                We accept all major credit cards and PayPal for your convenience and security.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-app-gray-900 mb-app-sm">
                What if I need to cancel?
              </h4>
              <p className="text-app-sm text-app-gray-600">
                You can cancel your subscription anytime. Your data remains accessible until your billing period ends.
              </p>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
